import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Lock, UploadCloud, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "http://localhost:8001";

const LOCKED_HEADERS = {
  Marble: [
    "Marble Sno", "Type of Marble", "Indian / Imported", "Color",
    "Short Description", "Long Description", "About the Marble",
    "Country of Origin", "Character", "Best for", "Image Link",
  ],
  Lot: ["Lot Sno", "Lot Number", "Marble Sno", "Thickness", "Price per Sq Ft", "Image Link"],
  Slab: ["Slab Sno", "Lot Sno", "Image Link"],
};

const formatTimestamp = (iso) => {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone: "Asia/Kolkata",
    }) + " IST";
  } catch (_) {
    return iso;
  }
};

const CountsRow = ({ label, counts }) => (
  <p className="font-sans text-sm text-brand-cream/85">
    <span className="text-brand-gold">{label}:</span>{" "}
    {counts?.inserted ?? 0} inserted · {counts?.updated ?? 0} updated · {counts?.skipped ?? 0} skipped
  </p>
);

const HeaderReferenceBlock = () => (
  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
    {Object.entries(LOCKED_HEADERS).map(([sheet, headers]) => (
      <div key={sheet} className="border border-brand-line bg-brand-surface/60 p-4 rounded">
        <p className="font-sans text-xs uppercase tracking-[0.2em] text-brand-gold mb-2">
          Sheet: {sheet}
        </p>
        <ol className="font-sans text-xs text-brand-cream/70 space-y-1 list-decimal list-inside">
          {headers.map((h) => (
            <li key={h}>{h}</li>
          ))}
        </ol>
        <p className="font-sans text-[11px] text-brand-cream/50 mt-3 pt-3 border-t border-brand-line leading-relaxed">
          <span className="text-brand-gold/80">Image Link:</span> Must be a direct image
          URL starting with <code className="text-brand-cream/70">https://</code>.
          Recommended format:{" "}
          <code className="text-brand-cream/70">https://ik.imagekit.io/[your-id]/[filename]</code>.
          Google Drive links are no longer supported.
        </p>
      </div>
    ))}
  </div>
);

const PasswordGate = ({ onAuthenticated }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setChecking(true);
    setError("");
    try {
      const res = await axios.post(`${BACKEND_URL}/api/admin/verify-password`, { password });
      if (res.data?.success) {
        onAuthenticated(password);
      } else {
        setError("Incorrect password. Please try again.");
      }
    } catch (_) {
      setError("Incorrect password. Please try again.");
    } finally {
      setChecking(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-brand-charcoal px-4">
      <form
        onSubmit={handleSubmit}
        data-testid="admin-password-form"
        className="w-full max-w-sm border border-brand-line bg-brand-surface/60 p-8 rounded"
      >
        <div className="flex items-center gap-2 mb-6 justify-center">
          <Lock className="h-5 w-5 text-brand-gold" />
          <h1 className="font-sans text-sm uppercase tracking-[0.2em] text-brand-cream">
            Admin Access
          </h1>
        </div>
        <input
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Enter admin password"
          autoFocus
          data-testid="admin-password-input"
          className="w-full bg-brand-charcoal text-brand-cream font-sans text-sm px-4 py-3 border border-brand-line focus:border-brand-gold/60 focus:outline-none rounded mb-3"
        />
        {error && (
          <p data-testid="admin-password-error" className="font-sans text-xs text-red-400 mb-3">
            {error}
          </p>
        )}
        <button
          type="submit"
          disabled={checking || !password}
          data-testid="admin-password-submit"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 bg-brand-gold text-brand-charcoal font-sans text-sm font-medium uppercase tracking-[0.15em] rounded transition-opacity disabled:opacity-50"
        >
          {checking ? <Loader2 className="h-4 w-4 animate-spin" /> : "Enter"}
        </button>
      </form>
    </div>
  );
};

const UploadHistory = ({ history }) => (
  <div data-testid="admin-upload-history" className="mt-10">
    <h2 className="font-sans text-xs uppercase tracking-[0.2em] text-brand-cream/60 mb-3">
      Upload History (last 5)
    </h2>
    {(!history || history.length === 0) && (
      <p className="font-sans text-sm text-brand-cream/40">No uploads yet.</p>
    )}
    <div className="space-y-2">
      {history?.map((h) => (
        <div
          key={h.id}
          data-testid="admin-history-item"
          className="border border-brand-line bg-brand-surface/40 p-3 rounded flex flex-col md:flex-row md:items-center md:justify-between gap-1"
        >
          <div className="flex items-center gap-2">
            {h.success ? (
              <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0" />
            ) : (
              <XCircle className="h-4 w-4 text-red-400 shrink-0" />
            )}
            <span className="font-sans text-xs text-brand-cream/80">{formatTimestamp(h.timestamp)}</span>
            <span className="font-sans text-xs text-brand-cream/40">{h.filename}</span>
          </div>
          <div className="font-sans text-xs text-brand-cream/60">
            {h.success && h.summary ? (
              <>
                Marble {h.summary.marble_types?.inserted ?? 0}/{h.summary.marble_types?.updated ?? 0}/{h.summary.marble_types?.skipped ?? 0}
                {" · "}
                Lots {h.summary.lots?.inserted ?? 0}/{h.summary.lots?.updated ?? 0}/{h.summary.lots?.skipped ?? 0}
                {" · "}
                Slabs {h.summary.slabs?.inserted ?? 0}/{h.summary.slabs?.updated ?? 0}/{h.summary.slabs?.skipped ?? 0}
              </>
            ) : (
              <span className="text-red-400/80">{h.error || "Failed"}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

const UploadUI = ({ adminPassword }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);

  const fetchHistory = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/upload-history`, {
        headers: { "X-Admin-Password": adminPassword },
      });
      setHistory(res.data?.history || []);
    } catch (_) {
      // silent - history is a convenience, not critical
    }
  }, [adminPassword]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
    setResult(null);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) return;
    setUploading(true);
    setResult(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${BACKEND_URL}/api/admin/upload-excel`, formData, {
        headers: {
          "X-Admin-Password": adminPassword,
          "Content-Type": "multipart/form-data",
        },
      });
      setResult(res.data);
    } catch (err) {
      setResult({
        success: false,
        error: err.response?.data?.error || "Upload failed. Please check the file and try again.",
        header_errors: err.response?.data?.header_errors || [],
      });
    } finally {
      setUploading(false);
      setFile(null);
      fetchHistory();
    }
  };

  return (
    <div className="min-h-screen bg-brand-charcoal px-4 md:px-10 py-12">
      <div className="max-w-4xl mx-auto">
        <h1 data-testid="admin-upload-heading" className="font-serif text-3xl text-brand-cream mb-1">
          Aurevia Stones — <span className="text-brand-gold italic">Content Manager</span>
        </h1>
        <p className="font-sans text-sm text-brand-cream/50 mb-8">
          Upload your Excel file to update the marble collection.
        </p>

        <div className="border border-brand-line bg-brand-surface/40 p-5 rounded mb-8">
          <p className="font-sans text-xs uppercase tracking-[0.15em] text-brand-cream/60 mb-2">
            Rules
          </p>
          <ul className="font-sans text-sm text-brand-cream/70 space-y-1 list-disc list-inside">
            <li>The file must be .xlsx or .xls</li>
            <li>It must contain exactly three sheets named: Marble, Lot, Slab</li>
            <li>Column headers must match exactly (name and position)</li>
            <li>Existing records will be updated. New records will be added.</li>
            <li>Records not present in the Excel will NOT be deleted.</li>
            <li>If any header is wrong, the upload will fail and no changes will be made.</li>
          </ul>
        </div>

        <div className="border border-brand-line bg-brand-surface/40 p-5 rounded mb-8">
          <p className="font-sans text-xs uppercase tracking-[0.15em] text-brand-cream/60 mb-3">
            How to add images
          </p>
          <ol className="font-sans text-sm text-brand-cream/70 space-y-2 list-decimal list-inside">
            <li>
              Upload your marble photos to Imagekit via{" "}
              <a
                href="https://imagekit.io"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-gold underline hover:text-brand-gold/80"
              >
                imagekit.io
              </a>{" "}
              and copy the generated URL for each image. The URL will start with:{" "}
              <code className="text-brand-gold/90">https://ik.imagekit.io/[your-id]/</code>
            </li>
            <li>Paste each URL into the Image Link column of your Excel for the corresponding marble type, lot, or slab.</li>
            <li>Upload your completed Excel using the section below.</li>
          </ol>
        </div>

        <HeaderReferenceBlock />

        <form
          onSubmit={handleUpload}
          data-testid="admin-upload-form"
          className="border border-dashed border-brand-gold/40 bg-brand-surface/20 p-6 rounded flex flex-col md:flex-row items-center gap-4"
        >
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileChange}
            data-testid="admin-file-input"
            className="flex-1 w-full font-sans text-sm text-brand-cream/80 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:bg-brand-gold file:text-brand-charcoal file:text-xs file:uppercase file:tracking-[0.15em] file:cursor-pointer"
          />
          <button
            type="submit"
            disabled={!file || uploading}
            data-testid="admin-upload-submit"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-brand-gold text-brand-charcoal font-sans text-sm font-medium uppercase tracking-[0.15em] rounded transition-opacity disabled:opacity-50 whitespace-nowrap"
          >
            {uploading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Processing...
              </>
            ) : (
              <>
                <UploadCloud className="h-4 w-4" /> Upload & Update Collection
              </>
            )}
          </button>
        </form>

        {result && (
          <div className="mt-6" data-testid="admin-upload-result">
            {result.success ? (
              <div
                data-testid="admin-upload-success"
                className="border border-green-500/30 bg-green-500/5 p-5 rounded"
              >
                <p className="font-sans text-sm text-green-400 flex items-center gap-2 mb-3">
                  <CheckCircle2 className="h-4 w-4" /> Upload successful
                </p>
                <CountsRow label="Marble Types" counts={result.summary?.marble_types} />
                <CountsRow label="Lots" counts={result.summary?.lots} />
                <CountsRow label="Slabs" counts={result.summary?.slabs} />
                {result.warnings && result.warnings.length > 0 && (
                  <div className="mt-4">
                    <p className="font-sans text-xs uppercase tracking-[0.15em] text-amber-400 mb-1">
                      Warnings
                    </p>
                    <ul className="font-sans text-xs text-amber-300/90 space-y-1">
                      {result.warnings.map((w, i) => (
                        <li key={i}>⚠ {w}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            ) : result.header_errors && result.header_errors.length > 0 ? (
              <div
                data-testid="admin-upload-header-errors"
                className="border border-amber-500/30 bg-amber-500/5 p-5 rounded"
              >
                <p className="font-sans text-sm text-amber-400 flex items-center gap-2 mb-3">
                  <XCircle className="h-4 w-4" /> Upload failed — no changes were made.
                </p>
                <ul className="font-sans text-xs text-amber-300/90 space-y-1">
                  {result.header_errors.map((e, i) => (
                    <li key={i}>✗ {e}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <div
                data-testid="admin-upload-general-error"
                className="border border-red-500/30 bg-red-500/5 p-5 rounded"
              >
                <p className="font-sans text-sm text-red-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" /> {result.error || "Upload failed. Please check the file and try again."}
                </p>
              </div>
            )}
          </div>
        )}

        <UploadHistory history={history} />
      </div>
    </div>
  );
};

const AdminUpload = () => {
  const [adminPassword, setAdminPassword] = useState(null);

  if (!adminPassword) {
    return <PasswordGate onAuthenticated={setAdminPassword} />;
  }
  return <UploadUI adminPassword={adminPassword} />;
};

export default AdminUpload;
